import React, { useEffect, useMemo, useState } from "react";
import {
  Mail, MailOpen, Inbox, Send, Paperclip, AlertCircle, Reply, Eye,
  ChevronDown, ChevronRight, Sparkles, RefreshCw, Loader2, X, Download,
} from "lucide-react";
import { api, API } from "../lib/api";
import { toast } from "sonner";

function fmt(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sec = (now - d) / 1000;
    if (sec < 60) return "à l'instant";
    if (sec < 3600) return `il y a ${Math.round(sec / 60)} min`;
    if (sec < 86400) return `il y a ${Math.round(sec / 3600)} h`;
    if (sec < 86400 * 7) return `il y a ${Math.round(sec / 86400)} j`;
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}
function fmtBytes(n) { if (!n) return ""; const u = ["B","KB","MB"]; let i=0; while(n>=1024 && i<2){n/=1024;i++;} return `${n.toFixed(0)} ${u[i]}`; }

export default function DossierEmails({ dossierId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [open, setOpen] = useState({});  // {msgId: true}
  const [replyTo, setReplyTo] = useState(null);
  const [compose, setCompose] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/dossier-emails/by-dossier/${dossierId}`);
      setItems(data.items || []);
    } catch (e) {
      toast.error("Impossible de charger les emails");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (dossierId) load(); /* eslint-disable-next-line */ }, [dossierId]);

  const refresh = async () => {
    setPolling(true);
    try {
      const { data } = await api.post("/dossier-emails/poll");
      if (data.ok) toast.success(`Indexation : ${data.matched || 0} email(s) rattaché(s)`);
      await load();
    } catch (e) {
      toast.error("Indexation impossible");
    } finally {
      setPolling(false);
    }
  };

  const toggle = async (m) => {
    const isOpen = !!open[m.message_id];
    setOpen({ ...open, [m.message_id]: !isOpen });
    if (!isOpen && !m._full) {
      try {
        const { data } = await api.get(`/dossier-emails/${encodeURIComponent(m.message_id)}`);
        setItems((arr) => arr.map((x) => x.message_id === m.message_id ? { ...x, ...data, _full: true } : x));
      } catch (e) {
        toast.error("Email illisible");
      }
    }
  };

  const downloadAtt = (m, att) => {
    const token = localStorage.getItem("norea_token");
    const url = `${API}/dossier-emails/${encodeURIComponent(m.message_id)}/attachment/${att.gridfs_id}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        if (!r.ok) throw new Error("HTTP " + r.status);
        const blob = await r.blob();
        const a = document.createElement("a");
        const objUrl = URL.createObjectURL(blob);
        a.href = objUrl; a.download = att.filename;
        document.body.appendChild(a); a.click();
        setTimeout(() => { URL.revokeObjectURL(objUrl); a.remove(); }, 1500);
      })
      .catch(() => toast.error("Téléchargement impossible"));
  };

  const counts = useMemo(() => ({
    total: items.length,
    unread: items.filter((i) => !i.read && i.direction === "in").length,
    urgent: items.filter((i) => i.urgent).length,
  }), [items]);

  return (
    <div className="bg-white rounded-lg border border-norea-border h-full flex flex-col min-w-0" data-testid="dossier-emails">
      <div className="flex items-center justify-between px-4 py-3 border-b border-norea-border">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-norea-primary" />
          <span className="font-heading font-semibold text-sm text-norea-text">
            Emails rattachés
          </span>
          <span className="text-xs text-norea-textMuted">({counts.total})</span>
          {counts.unread > 0 && (
            <span className="px-1.5 py-0.5 text-[10px] rounded bg-norea-primary text-white font-semibold">
              {counts.unread} non lu{counts.unread > 1 ? "s" : ""}
            </span>
          )}
          {counts.urgent > 0 && (
            <span className="px-1.5 py-0.5 text-[10px] rounded bg-status-erreurBg text-status-erreurText font-semibold inline-flex items-center gap-0.5">
              <AlertCircle className="w-3 h-3" />{counts.urgent} urgent{counts.urgent > 1 ? "s" : ""}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCompose(true)}
            className="px-2.5 py-1 text-xs rounded border border-norea-border bg-white hover:bg-norea-surfaceHover inline-flex items-center gap-1.5"
            data-testid="email-compose-btn"
          >
            <Send className="w-3.5 h-3.5" /> Nouveau
          </button>
          <button
            onClick={refresh}
            disabled={polling}
            className="px-2.5 py-1 text-xs rounded border border-norea-border bg-white hover:bg-norea-surfaceHover inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="email-refresh-btn"
          >
            {polling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Actualiser
          </button>
        </div>
      </div>

      <div className="flex-1 divide-y divide-norea-border max-h-[420px] overflow-y-auto" data-testid="dossier-emails-list">
        {loading ? (
          <div className="px-4 py-6 text-center text-xs text-norea-textMuted">
            <Loader2 className="inline w-4 h-4 animate-spin mr-2" /> Chargement…
          </div>
        ) : items.length === 0 ? (
          <div className="px-4 py-8 text-center text-xs text-norea-textMuted">
            Aucun email rattaché pour l'instant. <br />
            <span className="opacity-70">Les emails Gmail sont scannés toutes les 5 minutes, ou cliquez sur « Actualiser » pour indexer maintenant.</span>
          </div>
        ) : items.map((m) => (
          <EmailRow
            key={m.message_id}
            email={m}
            opened={!!open[m.message_id]}
            onToggle={() => toggle(m)}
            onReply={() => setReplyTo(m)}
            onDownloadAtt={(att) => downloadAtt(m, att)}
          />
        ))}
      </div>

      {replyTo && (
        <ReplyModal
          original={replyTo}
          onClose={() => setReplyTo(null)}
          onSent={() => { setReplyTo(null); load(); }}
        />
      )}

      {compose && (
        <ComposeModal
          dossierId={dossierId}
          onClose={() => setCompose(false)}
          onSent={() => { setCompose(false); load(); }}
        />
      )}
    </div>
  );
}

function EmailRow({ email, opened, onToggle, onReply, onDownloadAtt }) {
  const isIn = email.direction === "in";
  const Icon = email.read ? MailOpen : Mail;
  return (
    <div className="px-3 py-2.5">
      <button
        onClick={onToggle}
        className="w-full flex items-start gap-3 text-left hover:bg-norea-surfaceHover -mx-3 px-3 py-1 rounded"
        data-testid={`email-row-${email.message_id}`}
      >
        <div className="flex-shrink-0 mt-1">
          {opened ? <ChevronDown className="w-3.5 h-3.5 text-norea-textMuted" /> : <ChevronRight className="w-3.5 h-3.5 text-norea-textMuted" />}
        </div>
        <div className="flex-shrink-0 mt-0.5">
          {isIn ? (
            <Icon className={`w-4 h-4 ${email.read ? "text-norea-textMuted" : "text-norea-primary"}`} />
          ) : (
            <Send className="w-4 h-4 text-emerald-600" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-sm ${email.read ? "text-norea-text" : "text-norea-text font-semibold"} truncate`}>
              {isIn ? (email.from_name || email.from_email) : `→ ${(email.to_emails || []).join(", ")}`}
            </span>
            {email.urgent && (
              <span className="px-1 py-0.5 text-[9px] rounded bg-status-erreurBg text-status-erreurText font-semibold uppercase tracking-wider">urgent</span>
            )}
            {(email.missing_pieces_detected || []).length > 0 && (
              <span className="px-1 py-0.5 text-[9px] rounded bg-amber-100 text-amber-800 font-semibold inline-flex items-center gap-0.5">
                <Sparkles className="w-2.5 h-2.5" /> {email.missing_pieces_detected.length} pièce(s) demandée(s)
              </span>
            )}
            {(email.attachments || []).length > 0 && (
              <Paperclip className="w-3 h-3 text-norea-textMuted flex-shrink-0" />
            )}
          </div>
          <div className="text-xs text-norea-textMuted truncate">{email.subject || "(sans objet)"}</div>
          {email.resume_court && (
            <div className="text-[11px] text-norea-textMuted italic mt-0.5 truncate">💡 {email.resume_court}</div>
          )}
        </div>
        <div className="text-[11px] text-norea-textMuted flex-shrink-0 mt-1 whitespace-nowrap">{fmt(email.sent_at)}</div>
      </button>

      {opened && email._full && (
        <div className="ml-9 mt-2 mb-2 px-3 py-3 bg-norea-bg rounded-md border border-norea-border text-xs">
          <div className="flex items-center justify-between mb-2 text-[11px] text-norea-textMuted">
            <div className="space-y-0.5">
              <div><strong>De :</strong> {email.from_name ? `${email.from_name} <${email.from_email}>` : email.from_email}</div>
              <div><strong>À :</strong> {(email.to_emails || []).join(", ") || "—"}</div>
              {(email.cc_emails || []).length > 0 && <div><strong>Cc :</strong> {email.cc_emails.join(", ")}</div>}
            </div>
            {isIn && (
              <button
                onClick={onReply}
                className="px-2 py-1 text-xs rounded border border-norea-border bg-white hover:bg-norea-primary hover:text-white inline-flex items-center gap-1.5"
                data-testid={`email-reply-btn-${email.message_id}`}
              >
                <Reply className="w-3.5 h-3.5" /> Répondre
              </button>
            )}
          </div>

          {(email.missing_pieces_detected || []).length > 0 && (
            <div className="mb-2 p-2 rounded bg-amber-50 border border-amber-200">
              <div className="text-[11px] font-semibold text-amber-900 mb-1 inline-flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Pièces détectées par l'IA
              </div>
              <div className="flex flex-wrap gap-1">
                {email.missing_pieces_detected.map((p, i) => (
                  <span key={i} className="px-1.5 py-0.5 text-[10px] rounded bg-white border border-amber-300 text-amber-900">{p}</span>
                ))}
              </div>
              <div className="mt-1 text-[10px] text-amber-800">→ ajoutées automatiquement aux pièces manquantes du dossier</div>
            </div>
          )}

          {email.body_html ? (
            <div className="prose prose-sm max-w-none text-[12px] [&_a]:text-norea-primary"
                 dangerouslySetInnerHTML={{ __html: email.body_html }} />
          ) : (
            <pre className="whitespace-pre-wrap font-sans text-[12px] text-norea-text">{email.body_text || "(corps vide)"}</pre>
          )}

          {(email.attachments || []).length > 0 && (
            <div className="mt-3 pt-2 border-t border-norea-border">
              <div className="text-[11px] uppercase tracking-wider text-norea-textMuted mb-1.5">Pièces jointes ({email.attachments.length})</div>
              <div className="space-y-1">
                {email.attachments.map((a, i) => (
                  <button key={i}
                    onClick={() => onDownloadAtt(a)}
                    className="w-full flex items-center gap-2 px-2 py-1 rounded border border-norea-border bg-white hover:bg-norea-surfaceHover text-xs text-left"
                    data-testid={`email-att-${a.gridfs_id}`}
                  >
                    <Paperclip className="w-3 h-3 text-norea-textMuted" />
                    <span className="flex-1 truncate">{a.filename}</span>
                    <span className="text-[10px] text-norea-textMuted">{fmtBytes(a.size)}</span>
                    <Download className="w-3 h-3 text-norea-primary" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ReplyModal({ original, onClose, onSent }) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!body.trim()) { toast.error("Corps vide"); return; }
    setSending(true);
    try {
      await api.post(`/dossier-emails/${encodeURIComponent(original.message_id)}/reply`, { body });
      toast.success("Réponse envoyée");
      onSent();
    } catch (e) {
      toast.error("Envoi impossible : " + (e?.response?.data?.detail || e.message));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="email-reply-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl">
        <div className="px-5 py-3 border-b border-norea-border flex items-center justify-between">
          <div className="text-sm font-semibold">Répondre à {original.from_email}</div>
          <button onClick={onClose} className="text-norea-textMuted hover:text-norea-text" data-testid="email-reply-close-btn"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-3 border-b border-norea-border text-xs text-norea-textMuted">
          <div><strong>Sujet :</strong> Re: {original.subject}</div>
        </div>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={12}
          placeholder="Tapez votre réponse…"
          className="w-full px-4 py-3 text-sm border-none outline-none resize-none"
          data-testid="email-reply-body"
        />
        <div className="px-5 py-3 border-t border-norea-border flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs rounded border border-norea-border bg-white">Annuler</button>
          <button onClick={send} disabled={sending || !body.trim()}
                  className="px-3 py-1.5 text-xs rounded bg-norea-primary text-white inline-flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="email-reply-send-btn">
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Envoyer
          </button>
        </div>
      </div>
    </div>
  );
}

function ComposeModal({ dossierId, onClose, onSent }) {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!to || !subject || !body) { toast.error("Tous les champs sont requis"); return; }
    setSending(true);
    try {
      await api.post("/dossier-emails/compose", { dossier_id: dossierId, to, subject, body });
      toast.success("Email envoyé");
      onSent();
    } catch (e) {
      toast.error("Envoi impossible : " + (e?.response?.data?.detail || e.message));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="email-compose-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl">
        <div className="px-5 py-3 border-b border-norea-border flex items-center justify-between">
          <div className="text-sm font-semibold">Nouvel email rattaché au dossier</div>
          <button onClick={onClose} className="text-norea-textMuted hover:text-norea-text" data-testid="email-compose-close-btn"><X className="w-4 h-4" /></button>
        </div>
        <input
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="Destinataire"
          className="w-full px-4 py-2 text-sm border-b border-norea-border outline-none"
          data-testid="email-compose-to"
        />
        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Sujet"
          className="w-full px-4 py-2 text-sm border-b border-norea-border outline-none"
          data-testid="email-compose-subject"
        />
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={12}
          placeholder="Corps du message…"
          className="w-full px-4 py-3 text-sm border-none outline-none resize-none"
          data-testid="email-compose-body"
        />
        <div className="px-5 py-3 border-t border-norea-border flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs rounded border border-norea-border bg-white">Annuler</button>
          <button onClick={send} disabled={sending}
                  className="px-3 py-1.5 text-xs rounded bg-norea-primary text-white inline-flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="email-compose-send-btn">
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Envoyer
          </button>
        </div>
      </div>
    </div>
  );
}
